import { cva, type VariantProps } from "class-variance-authority";
import { type ExternalToast, Toaster as SonnerToaster, toast as sonnerToast } from "sonner";
import { CheckCircle, Close, ErrorSign, InfoOutline, Warning } from "../../Icons";
import { cn } from "../../tailwind/cn";
import { Button } from "../Button";

const Toaster = () => (
  <SonnerToaster
    className="pointer-events-auto"
    position="top-center"
    swipeDirections={["top", "left", "right"]}
    offset={{ top: 45 }}
    style={{ "--width": "700px" } as React.CSSProperties}
    toastOptions={{ classNames: { toast: "w-full" }, duration: 3000 }}
  />
);

export type ToastType = NonNullable<VariantProps<typeof toastVariants>["type"]>;
interface ToastProps extends VariantProps<typeof toastVariants> {
  id: string | number;
  message: string;
  description?: string;
  /** A way back for what the toast reports, such as Undo: it dismisses the toast once pressed. */
  action?: { label: string; onClick: () => void };
}

const toastVariants = cva(
  "flex w-full items-center gap-2 rounded-sm bg-white p-2 font-sans text-black shadow-back [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      type: {
        ERROR: "bg-error text-white",
        INFO: "bg-info text-white",
        SUCCESS: "bg-success text-white",
        WARNING: "bg-warning",
        LOADING: "bg-info text-white",
      },
    },
    defaultVariants: { type: "INFO" },
  },
);
const iconLookup: Record<ToastType, React.ReactNode> = {
  ERROR: <ErrorSign />,
  INFO: <InfoOutline />,
  SUCCESS: <CheckCircle />,
  WARNING: <Warning />,
  LOADING: <div className="loading-spinner size-4" />,
};

const Toast = ({ id, message, description, action, ...variantProps }: ToastProps) => (
  <div className={cn(toastVariants(variantProps))}>
    {iconLookup[variantProps.type ?? "INFO"]}
    {/* The words wrap; the action and the close button keep their size, so "Undo" is never split. */}
    <div className="flex min-w-0 flex-1 flex-col">
      <p className="font-semibold wrap-break-word md:text-large">{message}</p>
      {description && <p className="text-small md:text-normal">{description}</p>}
    </div>
    {action && (
      <Button
        variant="text"
        size="small"
        className="ml-auto shrink-0 whitespace-nowrap text-inherit underline hover:bg-white/20"
        onClick={() => {
          action.onClick();
          sonnerToast.dismiss(id);
        }}
      >
        {action.label}
      </Button>
    )}
    <Button
      icon
      onClick={() => sonnerToast.dismiss(id)}
      className={cn("shrink-0 text-inherit hover:bg-white/20", !action && "ml-auto")}
    >
      <Close />
    </Button>
  </div>
);

const toast = (props: Omit<ToastProps, "id">, options?: ExternalToast) =>
  sonnerToast.custom((toastId) => <Toast {...props} id={toastId} />, options);

interface ToastShorthandProps
  extends Omit<ToastProps, "id" | "message" | "type">, Pick<ExternalToast, "id" | "duration"> {}

const toSonnerOptions = (props?: ToastShorthandProps): ExternalToast | undefined =>
  props && { id: props.id, duration: props.duration };

toast.success = (message: string, props?: ToastShorthandProps) =>
  toast({ message, type: "SUCCESS", ...props }, toSonnerOptions(props));
toast.error = (message: string, props?: ToastShorthandProps) =>
  toast({ message, type: "ERROR", ...props }, toSonnerOptions(props));
toast.info = (message: string, props?: ToastShorthandProps) =>
  toast({ message, type: "INFO", ...props }, toSonnerOptions(props));
toast.warning = (message: string, props?: ToastShorthandProps) =>
  toast({ message, type: "WARNING", ...props }, toSonnerOptions(props));
toast.loading = (message: string, props?: ToastShorthandProps) =>
  toast({ message, type: "LOADING", ...props }, toSonnerOptions(props));
toast.dismiss = sonnerToast.dismiss;

export { toast, Toaster };
